import { getLayerCanvas, readPsd, type Layer as PsdLayer } from 'ag-psd'
import { makeShortId } from 'commons'

export const PSD_CANVAS_SIZE = 2048
/** 9:16 全屏模板可直接传的竖屏画布。宽 = 2048×9/16，导入时居中贴进方图。 */
export const PSD_PORTRAIT_WIDTH = 1152
/** 3:4 模板的 PSD 宽。按 1536×2048 原样导入，不留白。 */
export const PSD_3X4_WIDTH = 1536

export type PsdCanvasClip = {
  left: number
  top: number
  right: number
  bottom: number
}

/** 图层贴进 2048 方图时的位置。竖屏左右留白，超出 PSD 画布的像素不进空白。 */
export type PsdOutputPlace = {
  /** 原画布宽 */
  canvas: number
  /** 原画布高。方图等于 canvas，3:4 为 2048 */
  canvasHeight: number
  /** 只能导入这种画幅的模板；不填表示 1:1 / 9:16 都行 */
  fullscreenType?: 2 | 3
  offsetX: number
  offsetY: number
  clip: PsdCanvasClip
}

export const outputPlaceForPsd = (
  width: number,
  height: number,
): PsdOutputPlace | null => {
  if (width === PSD_CANVAS_SIZE && height === PSD_CANVAS_SIZE) {
    return {
      canvas: PSD_CANVAS_SIZE,
      canvasHeight: PSD_CANVAS_SIZE,
      offsetX: 0,
      offsetY: 0,
      clip: {
        left: 0,
        top: 0,
        right: PSD_CANVAS_SIZE,
        bottom: PSD_CANVAS_SIZE,
      },
    }
  }
  if (width === PSD_PORTRAIT_WIDTH && height === PSD_CANVAS_SIZE) {
    const offsetX = (PSD_CANVAS_SIZE - width) / 2
    return {
      canvas: PSD_CANVAS_SIZE,
      canvasHeight: PSD_CANVAS_SIZE,
      fullscreenType: 2,
      offsetX,
      offsetY: 0,
      clip: {
        left: offsetX,
        top: 0,
        right: offsetX + width,
        bottom: PSD_CANVAS_SIZE,
      },
    }
  }
  if (width === PSD_3X4_WIDTH && height === PSD_CANVAS_SIZE) {
    return {
      canvas: PSD_3X4_WIDTH,
      canvasHeight: PSD_CANVAS_SIZE,
      fullscreenType: 3,
      offsetX: 0,
      offsetY: 0,
      clip: {
        left: 0,
        top: 0,
        right: PSD_3X4_WIDTH,
        bottom: PSD_CANVAS_SIZE,
      },
    }
  }
  return null
}

/** PSD 尺寸能否导入该模板；不能时返回原因 */
export const psdPlaceError = (
  place: PsdOutputPlace | null,
  templateFullscreenType?: number | null,
): string | null => {
  const type = templateFullscreenType === 2 || templateFullscreenType === 3
    ? templateFullscreenType
    : 0
  if (type === 3) {
    return place?.fullscreenType === 3 ? null : '3:4 模板的 PSD 必须是 1536×2048'
  }
  if (!place) {
    return type === 2 ? '必须是 2048×2048，或 1152×2048' : '必须是 2048×2048'
  }
  if (place.fullscreenType === 3) return '1536×2048 只能导入 3:4 模板'
  if (place.fullscreenType === 2 && type !== 2) {
    return '1152×2048 只能导入 9:16 全屏模板'
  }
  return null
}

const FULLSCREEN_PAD = 4
const UNMATCHED_PLAY_BASE = 9000
export const DEFAULT_LAYER_COVER = 'UWj'

export type LayerIconHit = {
  id: string
  code?: string
  name?: string
  name_en?: string
  p?: number
  z?: number
  gid?: string
  max?: number
  opt?: boolean
  body?: boolean | number
  water?: boolean | number
}

export type PsdImageItem = {
  id: string
  name: string
  left: number
  top: number
  fillsCanvas: boolean
  /** 压缩后的图层。上传时再解码，避免把整份 PSD 一次展开进内存。 */
  layer: PsdLayer
  preview: string
}

export type PsdFolderItem = {
  name: string
  zIndex: number
  playIndex: number
  match: 'hit' | 'miss' | 'ambiguous'
  icon?: LayerIconHit
  images: PsdImageItem[]
}

export type PsdParseResult = {
  width: number
  height: number
  folders: PsdFolderItem[]
  skippedRoot: string[]
  skippedEmpty: string[]
  skippedNoPixels: string[]
}

const isGroup = (layer: PsdLayer) => Array.isArray(layer.children)

const layerName = (layer: PsdLayer) => (layer.name || '').trim() || '未命名'

const fillsCanvas = (
  layer: PsdLayer,
  width: number,
  height: number,
): boolean => {
  const left = layer.left ?? 0
  const top = layer.top ?? 0
  const right = layer.right ?? left + (layer.canvas?.width ?? 0)
  const bottom = layer.bottom ?? top + (layer.canvas?.height ?? 0)
  return (
    left <= FULLSCREEN_PAD &&
    top <= FULLSCREEN_PAD &&
    right >= width - FULLSCREEN_PAD &&
    bottom >= height - FULLSCREEN_PAD
  )
}

/** 只看图层框和压缩通道，不解码像素。蒙版通道不算画面。 */
const hasPixelData = (layer: PsdLayer) => {
  const left = layer.left ?? 0
  const top = layer.top ?? 0
  const right = layer.right ?? left
  const bottom = layer.bottom ?? top
  if (right - left < 1 || bottom - top < 1) return false
  const channels = layer.rawData?.channels
  if (
    channels?.some(
      (channel) =>
        channel.id >= -1 && (channel.data?.byteLength ?? 0) > 0,
    )
  ) {
    return true
  }
  return Boolean(layer.canvas && layer.canvas.width >= 1 && layer.canvas.height >= 1)
}

/** 解码单张图层。调用方用完要释放画布，压缩数据还在，失败可以再解一次。 */
export const decodeLayerCanvas = (layer: PsdLayer): HTMLCanvasElement => {
  const canvas =
    layer.canvas && layer.canvas.width >= 1 && layer.canvas.height >= 1
      ? layer.canvas
      : getLayerCanvas(layer)
  if (!canvas || canvas.width < 1 || canvas.height < 1) {
    throw new Error(`图层没有像素：${layerName(layer)}`)
  }
  return canvas
}

/** 卡片用的小缩略图。不保存整张图。 */
export const previewFromCanvas = (canvas: HTMLCanvasElement) => {
  const max = 72
  const scale = Math.min(1, max / Math.max(canvas.width, canvas.height, 1))
  const width = Math.max(1, Math.round(canvas.width * scale))
  const height = Math.max(1, Math.round(canvas.height * scale))
  const thumb = document.createElement('canvas')
  thumb.width = width
  thumb.height = height
  const ctx = thumb.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(canvas, 0, 0, width, height)
  const url = thumb.toDataURL('image/webp', 0.6)
  thumb.width = 0
  thumb.height = 0
  return url
}

const collectImages = (
  node: PsdLayer,
  width: number,
  height: number,
  images: PsdImageItem[],
  skippedNoPixels: string[],
) => {
  if (isGroup(node)) {
    for (const child of node.children || []) {
      collectImages(child, width, height, images, skippedNoPixels)
    }
    return
  }
  if (!hasPixelData(node)) {
    skippedNoPixels.push(layerName(node))
    return
  }
  images.push({
    id: makeShortId(8),
    name: layerName(node),
    left: node.left ?? 0,
    top: node.top ?? 0,
    fillsCanvas: fillsCanvas(node, width, height),
    layer: node,
    preview: '',
  })
}

const norm = (value?: string) => (value || '').trim()

export const matchFolderName = (
  folderName: string,
  icons: LayerIconHit[],
): { match: PsdFolderItem['match']; icon?: LayerIconHit } => {
  const key = norm(folderName)
  if (!key) return { match: 'miss' }
  const hits = icons.filter((icon) => norm(icon.name) === key)
  if (hits.length === 1) return { match: 'hit', icon: hits[0] }
  if (hits.length > 1) return { match: 'ambiguous' }
  return { match: 'miss' }
}

export const parsePsdFile = async (
  file: File,
  icons: LayerIconHit[],
): Promise<PsdParseResult> => {
  const buffer = await file.arrayBuffer()
  // useRawData：只保留压缩通道。一次把全部图层展开成位图会超过 ag-psd 的 2GB 上限。
  const psd = readPsd(buffer, {
    skipCompositeImageData: true,
    skipThumbnail: true,
    skipLinkedFilesData: true,
    useRawData: true,
  })
  const width = psd.width
  const height = psd.height
  const skippedRoot: string[] = []
  const skippedEmpty: string[] = []
  const skippedNoPixels: string[] = []
  const rawFolders: PsdLayer[] = []

  for (const child of psd.children || []) {
    if (isGroup(child)) {
      rawFolders.push(child)
    } else {
      skippedRoot.push(layerName(child))
    }
  }

  const folders: PsdFolderItem[] = []
  rawFolders.forEach((folder, indexFromTop) => {
    const images: PsdImageItem[] = []
    collectImages(folder, width, height, images, skippedNoPixels)
    const name = layerName(folder)
    if (images.length === 0) {
      skippedEmpty.push(name)
      return
    }
    const { match, icon } = matchFolderName(name, icons)
    folders.push({
      name,
      // children 从上到下；实测越靠上应 z 越小，和 PSD 列表方向相反
      zIndex: indexFromTop,
      playIndex:
        match === 'hit' && icon?.p != null
          ? Number(icon.p)
          : UNMATCHED_PLAY_BASE + indexFromTop,
      match,
      icon,
      images,
    })
  })

  return {
    width,
    height,
    folders,
    skippedRoot,
    skippedEmpty,
    skippedNoPixels,
  }
}

export const canvasToDataUrl = (
  canvas: HTMLCanvasElement,
  type = 'image/png',
): Promise<string> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('canvas toBlob failed'))
        return
      }
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = reject
      reader.readAsDataURL(blob)
    }, type)
  })

export const compositeToCanvas = (
  source: HTMLCanvasElement,
  left: number,
  top: number,
  size = PSD_CANVAS_SIZE,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas context unavailable')
  ctx.drawImage(source, left, top)
  return canvas
}
